// ============================================================
//  Topbar.tsx — Custom frameless title bar + nav bar
//  FIX: -webkit-app-region via CSS classes (React inline styles
//       do NOT apply this Electron-specific property)
// ============================================================
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSidebar }  from "./MainLayout";
import { getBackendUrl } from "../config";
import { useBranding } from "../utils/BrandingContext";
import apiClient from "../utils/apiClient";
import dialog from "../utils/dialog";
import {
  COMPANY_CONTEXT_CHANGED_EVENT,
  formatSelectedCompanyDateTime,
  getSelectedCompanyId,
  getSelectedTenantId,
  switchAuthorizedCompany,
} from "../utils/companyContext";

// ── Alert Bell — Smart Notification Widget ───────────────────
const AlertBell = () => {
  const navigate = useNavigate();
  const [open, setOpen]         = useState(false);
  const [summary, setSummary]   = useState<any>(null);
  const [alerts, setAlerts]     = useState<any[]>([]);
  const [loading, setLoading]   = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const CID = useCallback(() => getSelectedCompanyId(), []);
  const H   = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    "X-Tenant-ID": String(getSelectedTenantId() || ""),
    "X-Company-ID": String(CID()),
  }), [CID]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/alerts/summary`, { headers: H() });
      if (res.ok) setSummary(await res.json());
    } catch {}
  }, [H]);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${getBackendUrl()}/api/alerts/list`, { headers: H() });
      if (res.ok) {
        const d = await res.json();
        setAlerts(d.alerts || []);
      }
    } catch {}
    finally { setLoading(false); }
  }, [H]);

  // Fetch summary on mount + every 60s
  useEffect(() => {
    fetchSummary();
    const t = setInterval(fetchSummary, 60000);
    return () => clearInterval(t);
  }, [fetchSummary]);

  // Fetch alerts when dropdown opens
  useEffect(() => {
    if (open) fetchAlerts();
  }, [open, fetchAlerts]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const total    = summary?.total    || 0;
  const critical = summary?.critical || 0;

  const typeColor = (t: string) => t === "CRITICAL" ? "#DC2626" : t === "WARNING" ? "#D97706" : "#2563EB";
  const typeBg    = (t: string) => t === "CRITICAL" ? "#FEF2F2" : t === "WARNING" ? "#FFFBEB" : "#EFF6FF";

  return (
    <div ref={ref} style={{ position: "relative", height: "100%" }}>
      {/* Bell Button */}
      <button
        className="app-no-drag"
        onClick={() => setOpen(o => !o)}
        title={`${total} Alerts`}
        style={{
          position: "relative",
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 46, height: "100%",
          background: open ? "#1E3A5F" : "transparent",
          border: "none",
          borderLeft: "1px solid #1E3A5F",
          borderRight: "1px solid #1E3A5F",
          cursor: "pointer",
          transition: "background 0.15s",
          padding: 0,
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = "#1E3A5F"; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = "transparent"; }}
      >
        {/* Bell SVG */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke={total > 0 ? "#FBBF24" : "#94A3B8"} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {/* Badge */}
        {total > 0 && (
          <div style={{
            position: "absolute", top: 8, right: 6,
            minWidth: 16, height: 16, borderRadius: 8,
            background: critical > 0 ? "#DC2626" : "#D97706",
            color: "#fff", fontSize: 9, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 3px",
            border: "1.5px solid #0F172A",
            lineHeight: 1,
          }}>
            {total > 99 ? "99+" : total}
          </div>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div style={{
          position: "fixed",
          top: 52, right: 160,
          width: 380,
          maxHeight: 480,
          background: "#1E293B",
          border: "1px solid #334155",
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          zIndex: 9999,
          overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          {/* Header */}
          <div style={{
            padding: "14px 16px 10px",
            borderBottom: "1px solid #334155",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="#FBBF24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#F1F5F9" }}>Smart Alerts</span>
            </div>
            {/* Summary pills */}
            <div style={{ display: "flex", gap: 6 }}>
              {critical > 0 && (
                <span style={{ background: "#7F1D1D", color: "#FCA5A5", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 12 }}>
                  {critical} Critical
                </span>
              )}
              {(summary?.warning || 0) > 0 && (
                <span style={{ background: "#78350F", color: "#FCD34D", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 12 }}>
                  {summary.warning} Warning
                </span>
              )}
            </div>
          </div>

          {/* Alert list */}
          <div style={{ overflowY: "auto", flex: 1, padding: "6px 0" }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: "center", color: "#64748B", fontSize: 13 }}>Loading alerts…</div>
            ) : alerts.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
                <div style={{ color: "#64748B", fontSize: 13 }}>All clear — no alerts!</div>
              </div>
            ) : (
              alerts.map((a, i) => (
                <div
                  key={a.id || i}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 10,
                    padding: "10px 14px",
                    borderBottom: "1px solid #1E293B",
                    background: "transparent",
                    transition: "background 0.12s",
                    cursor: a.action_url ? "pointer" : "default",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = "#0F172A")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                  onClick={() => {
                    if (a.action_url) { navigate(a.action_url); setOpen(false); }
                  }}
                >
                  {/* Type dot */}
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%", marginTop: 5, flexShrink: 0,
                    background: typeColor(a.type),
                    boxShadow: `0 0 6px ${typeColor(a.type)}88`,
                  }} />
                  {/* Icon + content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                      <span style={{ fontSize: 13 }}>{a.icon}</span>
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        color: typeColor(a.type),
                        background: typeBg(a.type),
                        padding: "1px 6px", borderRadius: 4,
                      }}>{a.title}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#94A3B8", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.message}
                    </div>
                  </div>
                  {/* Arrow */}
                  {a.action_url && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 4 }}>
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: "10px 16px",
            borderTop: "1px solid #334155",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <span style={{ fontSize: 11, color: "#64748B" }}>{alerts.length} alerts total</span>
            <button
              onClick={() => { fetchAlerts(); }}
              style={{ background: "transparent", border: "none", color: "#6366F1", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
            >
              ↺ Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Notification Bell — M33 (in-app feed) ───────────────────
const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await apiClient.get("/api/notifications-v2/inbox/unread-count");
      setUnread(res.data?.unread || 0);
    } catch {}
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/api/notifications-v2/inbox", { params: { limit: 10 } });
      setItems(res.data?.data || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUnread(); const t = setInterval(fetchUnread, 30000); return () => clearInterval(t); }, [fetchUnread]);
  useEffect(() => { if (open) fetchItems(); }, [open, fetchItems]);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const markRead = async (id: number, actionUrl?: string) => {
    try {
      await apiClient.post(`/api/notifications-v2/inbox/${id}/read`);
      fetchUnread(); fetchItems();
      if (actionUrl) { setOpen(false); navigate(actionUrl); }
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await apiClient.post("/api/notifications-v2/inbox/read-all");
      fetchUnread(); fetchItems();
    } catch {}
  };

  const priorityColor = (p: string) =>
    p === "URGENT" ? "#DC2626" : p === "HIGH" ? "#F97316" : p === "NORMAL" ? "#0EA5E9" : "#64748B";

  return (
    <div ref={ref} style={{ position: "relative", height: "100%" }}>
      <button
        className="app-no-drag"
        onClick={() => setOpen(o => !o)}
        title={`${unread} unread notifications`}
        style={{
          position: "relative", display: "flex", alignItems: "center", justifyContent: "center",
          width: 46, height: "100%",
          background: open ? "#1E3A5F" : "transparent",
          border: "none", borderRight: "1px solid #1E3A5F",
          cursor: "pointer", transition: "background 0.15s", padding: 0,
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = "#1E3A5F"; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = "transparent"; }}
      >
        {/* Inbox/Mail SVG */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke={unread > 0 ? "#60A5FA" : "#94A3B8"} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
          <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
        </svg>
        {unread > 0 && (
          <div style={{
            position: "absolute", top: 8, right: 6,
            minWidth: 16, height: 16, borderRadius: 8,
            background: "#2563EB", color: "#fff", fontSize: 9, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 3px", border: "1.5px solid #0F172A", lineHeight: 1,
          }}>
            {unread > 99 ? "99+" : unread}
          </div>
        )}
      </button>

      {open && (
        <div style={{
          position: "fixed", top: 52, right: 210, width: 400, maxHeight: 500,
          background: "#1E293B", border: "1px solid #334155", borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)", zIndex: 9999, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid #334155",
                        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
                <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#F1F5F9" }}>Notifications</span>
              {unread > 0 && (
                <span style={{ background: "#1E40AF", color: "#BFDBFE", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 12 }}>
                  {unread} unread
                </span>
              )}
            </div>
            <button onClick={markAllRead} disabled={unread === 0}
              style={{ background: "transparent", border: "none", color: unread > 0 ? "#60A5FA" : "#475569", fontSize: 11, fontWeight: 600, cursor: unread > 0 ? "pointer" : "default" }}>
              Mark all read
            </button>
          </div>

          <div style={{ overflowY: "auto", flex: 1, padding: "6px 0" }}>
            {loading ? <div style={{ padding: 24, textAlign: "center", color: "#64748B", fontSize: 13 }}>Loading…</div>
            : items.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
                <div style={{ color: "#64748B", fontSize: 13 }}>No notifications yet</div>
              </div>
            ) : items.map((n, i) => (
              <div key={n.id || i} onClick={() => markRead(n.id, n.action_url)}
                style={{
                  display: "flex", gap: 10, padding: "10px 14px", cursor: "pointer",
                  background: n.read_at ? "transparent" : "#1E3A5F",
                  borderBottom: "1px solid #334155",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#334155"}
                onMouseLeave={e => e.currentTarget.style.background = n.read_at ? "transparent" : "#1E3A5F"}>
                <div style={{ width: 4, alignSelf: "stretch", background: priorityColor(n.priority), borderRadius: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: n.read_at ? 500 : 700, fontSize: 13, color: "#F1F5F9", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {n.icon || "🔔"} {n.title}
                    </span>
                    {!n.read_at && <span style={{ width: 7, height: 7, background: "#60A5FA", borderRadius: "50%", flexShrink: 0 }} />}
                  </div>
                  {n.body && <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.body}</div>}
                  <div style={{ fontSize: 10, color: "#64748B", marginTop: 4 }}>
                    {n.entity_doc_no && <span style={{ marginRight: 8 }}>📎 {n.entity_doc_no}</span>}
                    {n.created_at && formatSelectedCompanyDateTime(n.created_at, { dateStyle: "short", timeStyle: "short" })}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ padding: "10px 16px", borderTop: "1px solid #334155",
                        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 11, color: "#64748B" }}>{items.length} recent</span>
            <button onClick={() => { setOpen(false); navigate("/notifications"); }}
              style={{ background: "transparent", border: "none", color: "#60A5FA", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              View all →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Cache Status — Redis health + manual refresh ────────────
const CacheStatus = () => {
  const [open, setOpen] = useState(false);
  const [health, setHealth] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const H = () => ({
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  });

  const fetchAll = useCallback(async () => {
    try {
      const [h, s] = await Promise.all([
        fetch(`${getBackendUrl()}/api/admin/cache/health`, { headers: H() }).then(r => r.ok ? r.json() : null),
        fetch(`${getBackendUrl()}/api/admin/cache/stats`,  { headers: H() }).then(r => r.ok ? r.json() : null),
      ]);
      setHealth(h);
      setStats(s);
    } catch {}
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);   // refresh every 30s
    return () => clearInterval(t);
  }, [fetchAll]);

  // Click outside closes popover
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const refresh = async () => {
    setLoading(true);
    try {
      await fetch(`${getBackendUrl()}/api/admin/cache/refresh`, {
        method: "POST", headers: H(),
      });
      await fetchAll();
    } finally { setLoading(false); }
  };

  const flushAll = async () => {
    if (!await dialog.confirm("Flush ALL caches? Next request will hit DB directly (slower for first user).")) return;
    setLoading(true);
    try {
      await fetch(`${getBackendUrl()}/api/admin/cache/flush-all`, {
        method: "POST", headers: H(),
      });
      await fetchAll();
    } finally { setLoading(false); }
  };

  const status   = health?.status;
  const healthy  = status === "healthy";
  const dotColor = healthy ? "#10B981" : status === "unavailable" ? "#94A3B8" : "#EF4444";
  const total    = stats?.total_keys ?? 0;
  const groups   = stats?.groups || {};

  return (
    <div ref={ref} className="app-no-drag" style={{ position: "relative", marginRight: 8 }}>
      <button
        onClick={() => setOpen(!open)}
        title={healthy ? `Redis healthy • ${total} keys cached` : "Redis unavailable"}
        style={{
          background: "transparent", border: "none",
          padding: "4px 8px", cursor: "pointer", color: "#fff",
          display: "flex", alignItems: "center", gap: 5,
          fontSize: 11, fontWeight: 600,
        }}
      >
        <span style={{
          width: 8, height: 8, borderRadius: "50%", background: dotColor,
          boxShadow: healthy ? `0 0 6px ${dotColor}` : "none",
        }} />
        <span style={{ fontSize: 11, opacity: .8 }}>⚡ {total}</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: 36, right: 0,
          width: 280, background: "#1E293B", color: "#fff",
          border: "1px solid #334155", borderRadius: 6,
          boxShadow: "0 12px 36px rgba(0,0,0,.4)", zIndex: 999,
        }}>
          {/* Header */}
          <div style={{ padding: "12px 14px", borderBottom: "1px solid #334155" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>⚡ Cache Status</span>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 8px",
                borderRadius: 4, background: healthy ? "#064E3B" : "#7F1D1D",
                color: healthy ? "#10B981" : "#EF4444",
              }}>
                {healthy ? "● ACTIVE" : "● OFFLINE"}
              </span>
            </div>
            <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>
              {healthy
                ? `Redis ${health?.version || ""} • ${health?.host || ""}`
                : (health?.message || "Memurai/Redis not running")}
            </div>
          </div>

          {/* Stats */}
          <div style={{ padding: "10px 14px", maxHeight: 240, overflowY: "auto" }}>
            <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 6 }}>
              Cached Keys (Total: <b style={{ color: "#fff" }}>{total}</b>)
            </div>
            {Object.keys(groups).length === 0 ? (
              <div style={{ fontSize: 11, color: "#64748B", padding: 8, textAlign: "center" }}>
                No keys cached yet. Open a dashboard to populate.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {Object.entries(groups).map(([g, c]) => (
                  <div key={g} style={{
                    display: "flex", justifyContent: "space-between",
                    fontSize: 11, padding: "4px 8px",
                    background: "#0F172A", borderRadius: 3,
                  }}>
                    <span style={{ color: "#CBD5E1" }}>{g}</span>
                    <span style={{ color: "#10B981", fontWeight: 700 }}>{String(c)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ padding: "10px 14px", borderTop: "1px solid #334155", display: "flex", gap: 6 }}>
            <button onClick={refresh} disabled={loading || !healthy}
                    style={{
                      flex: 1, background: "#1D4ED8", color: "#fff", border: 0,
                      padding: "6px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                      cursor: loading || !healthy ? "not-allowed" : "pointer",
                      opacity: loading || !healthy ? .5 : 1,
                    }}>
              ↻ Refresh Dashboards
            </button>
            <button onClick={flushAll} disabled={loading || !healthy}
                    title="Clear ALL cache keys"
                    style={{
                      background: "#7F1D1D", color: "#fff", border: 0,
                      padding: "6px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                      cursor: loading || !healthy ? "not-allowed" : "pointer",
                      opacity: loading || !healthy ? .5 : 1,
                    }}>
              🗑️ Flush All
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

declare global {
  interface Window {
    electronAPI?: {
      isElectron?: boolean;
      windowMinimize?: () => void;
      windowMaximize?: () => void;
      windowClose?:    () => void;
    };
  }
}

type CompanyAccess = {
  id: number;
  company_code?: string;
  company_name?: string;
  industry_type?: string;
  base_currency?: string;
  display_locale?: string | null;
  timezone_name?: string | null;
  numbering_system?: string | null;
  access_role?: string;
  is_default?: boolean;
};

const COMPANY_CONTEXT_EVENT = "aviraapro:companies-changed";
const SELECTED_COMPANY_CONTEXT_EVENT = COMPANY_CONTEXT_CHANGED_EVENT;

const readJson = (key: string): any => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const syncSelectedCompany = (
  company: CompanyAccess,
  { emitChangeEvent = true }: { emitChangeEvent?: boolean } = {},
): boolean => {
  const previousCompanyId = localStorage.getItem("company_id") || localStorage.getItem("erp_company_id") || "";
  localStorage.setItem("company_id", String(company.id));
  localStorage.setItem("erp_company_id", String(company.id));
  if (company.company_code) localStorage.setItem("erp_company_code", String(company.company_code));
  if (company.company_name) localStorage.setItem("erp_company_name", String(company.company_name));
  if (company.industry_type) localStorage.setItem("erp_industry_type", String(company.industry_type));
  const baseCurrency = String(company.base_currency || "").trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(baseCurrency)) {
    localStorage.setItem("erp_company_base_currency", baseCurrency);
  } else {
    localStorage.removeItem("erp_company_base_currency");
  }
  let displayLocale = String(company.display_locale || "").trim();
  try {
    if (displayLocale) {
      new Intl.NumberFormat(displayLocale);
      localStorage.setItem("erp_company_display_locale", displayLocale);
    } else {
      localStorage.removeItem("erp_company_display_locale");
    }
  } catch {
    displayLocale = "";
    localStorage.removeItem("erp_company_display_locale");
  }
  let timezoneName = String(company.timezone_name || "").trim();
  try {
    if (timezoneName) {
      new Intl.DateTimeFormat(undefined, { timeZone: timezoneName });
      localStorage.setItem("erp_company_timezone", timezoneName);
    } else {
      localStorage.removeItem("erp_company_timezone");
    }
  } catch {
    timezoneName = "";
    localStorage.removeItem("erp_company_timezone");
  }
  let numberingSystem = String(company.numbering_system || "").trim().toUpperCase();
  if (["INTERNATIONAL", "INDIAN", "DIGITS"].includes(numberingSystem)) {
    localStorage.setItem("erp_company_numbering_system", numberingSystem);
  } else {
    numberingSystem = "";
    localStorage.removeItem("erp_company_numbering_system");
  }
  if (company.access_role) {
    localStorage.setItem("company_access_role", String(company.access_role));
    localStorage.setItem("erp_access_role", String(company.access_role));
    localStorage.setItem(`erp_company_access_role_${company.id}`, String(company.access_role));
  }

  const user = readJson("erp_user") || {};
  localStorage.setItem("erp_user", JSON.stringify({
    ...user,
    company_id: company.id,
    company_code: company.company_code,
    company_name: company.company_name,
    industry_type: company.industry_type || user.industry_type,
    base_currency: baseCurrency || undefined,
    display_locale: displayLocale || undefined,
    timezone_name: timezoneName || undefined,
    numbering_system: numberingSystem || undefined,
    access_role: company.access_role,
  }));

  const changed = previousCompanyId !== String(company.id);
  if (changed) {
    localStorage.removeItem("erp_enabled_modules");
    localStorage.removeItem("erp_enabled_routes");
    localStorage.removeItem("erp_disabled_routes");
  }
  if (emitChangeEvent) {
    window.dispatchEvent(new CustomEvent(SELECTED_COMPANY_CONTEXT_EVENT, {
      detail: { company_id: company.id, source: "selected-company-sync" },
    }));
  }
  return changed;
};

const CompanySelector = () => {
  const [companies, setCompanies] = useState<CompanyAccess[]>([]);
  const [selectedId, setSelectedId] = useState(String(getSelectedCompanyId() || ""));
  const [companyContextLocked, setCompanyContextLocked] = useState(true);
  const [loading, setLoading] = useState(false);
  const activeCompanyIdRef = useRef(getSelectedCompanyId());

  const loadContext = useCallback(async () => {
    const token = localStorage.getItem("token") || localStorage.getItem("erp_token");
    if (!token) return;
    setLoading(true);
    try {
      const res = await apiClient.get("/api/tenants/me/context");
      const data = res.data || {};
      const list: CompanyAccess[] = Array.isArray(data.companies)
        ? data.companies.filter((company: CompanyAccess) => (
          Number.isInteger(company?.id)
          && company.id > 0
          && /^[A-Z]{3}$/.test(String(company.base_currency || "").trim().toUpperCase())
        ))
        : [];
      const selected = data.selected_company || null;
      const tenant = data.tenant || null;
      setCompanies(list);
      setCompanyContextLocked(data.company_context_locked === true);
      if (tenant?.tenant_name) localStorage.setItem("erp_tenant_name", String(tenant.tenant_name));
      if (tenant?.tenant_code) {
        localStorage.setItem("tenant_code", String(tenant.tenant_code));
        localStorage.setItem("erp_tenant_code", String(tenant.tenant_code));
      }
      if (tenant?.id) {
        localStorage.setItem("tenant_id", String(tenant.id));
        localStorage.setItem("erp_tenant_id", String(tenant.id));
      }

      const storedId = localStorage.getItem("company_id") || localStorage.getItem("erp_company_id") || "";
      const selectedFromList = list.find((c: CompanyAccess) => String(c.id) === storedId)
        || (selected?.id ? list.find((c: CompanyAccess) => String(c.id) === String(selected.id)) : null)
        || null;
      // Only a row returned in the signed user's authorized company list may
      // become selectable or be synchronized into browser context.
      const nextSelected = selectedFromList;
      if (nextSelected?.id) {
        setSelectedId(String(nextSelected.id));
        syncSelectedCompany(nextSelected);
      }
    } catch {
      setCompanies([]);
      setCompanyContextLocked(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const reloadForChangedCompany = (nextCompanyId: number) => {
      if (!Number.isInteger(nextCompanyId) || nextCompanyId <= 0) return false;
      const previousCompanyId = activeCompanyIdRef.current;
      activeCompanyIdRef.current = nextCompanyId;
      if (previousCompanyId > 0 && previousCompanyId !== nextCompanyId) {
        // Module-level CID constants and mounted page rows cannot be safely
        // invalidated in-place. A full reload guarantees a clean company scope.
        window.location.reload();
        return true;
      }
      return false;
    };
    const onSelectedCompanyChange = (event: Event) => {
      const detail = (event as CustomEvent<{ company_id?: number }>).detail;
      reloadForChangedCompany(Number(detail?.company_id || getSelectedCompanyId()));
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === "company_id" || event.key === "erp_company_id") {
        if (reloadForChangedCompany(Number(event.newValue || getSelectedCompanyId()))) return;
      }
      loadContext();
    };

    window.addEventListener(SELECTED_COMPANY_CONTEXT_EVENT, onSelectedCompanyChange);
    window.addEventListener(COMPANY_CONTEXT_EVENT, loadContext);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", loadContext);
    loadContext();
    return () => {
      window.removeEventListener(SELECTED_COMPANY_CONTEXT_EVENT, onSelectedCompanyChange);
      window.removeEventListener(COMPANY_CONTEXT_EVENT, loadContext);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", loadContext);
    };
  }, [loadContext]);

  const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const targetCompanyId = Number(event.target.value);
    const result = switchAuthorizedCompany({
      authorizedCompanies: companies,
      currentCompanyId: getSelectedCompanyId(),
      targetCompanyId,
      companyContextLocked,
      // Explicit selector reloads itself exactly once. Suppress the shared
      // change event so its listener cannot schedule a second reload.
      syncCompany: (company) => syncSelectedCompany(company, { emitChangeEvent: false }),
      reload: () => window.location.reload(),
    });
    if (result !== "switched") setSelectedId(String(getSelectedCompanyId() || ""));
  };

  const selected = companies.find((company) => String(company.id) === selectedId);
  const label = selected?.company_name || localStorage.getItem("erp_company_name") || "Company";

  return (
    <div
      className="app-no-drag"
      title={companyContextLocked ? "Company is locked for this login session" : "Switch selected company"}
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        width: 230,
        minWidth: 180,
        maxWidth: 260,
        height: "100%",
        padding: "0 10px",
        borderLeft: "1px solid rgba(148,163,184,0.18)",
      }}
    >
      <div style={{
        color: "#93C5FD",
        fontSize: 9,
        fontWeight: 800,
        lineHeight: 1,
        marginBottom: 3,
        textTransform: "uppercase",
        letterSpacing: 0.6,
      }}>
        {companyContextLocked ? "Company (Locked)" : "Switch Company"}
      </div>
      <select
        aria-label="Selected company"
        value={selectedId}
        onChange={onChange}
        disabled={loading || companyContextLocked || companies.length < 2}
        style={{
          width: "100%",
          height: 28,
          border: "1px solid rgba(96,165,250,0.55)",
          borderRadius: 6,
          background: "rgba(15,23,42,0.92)",
          color: "#E2E8F0",
          fontSize: 12,
          fontWeight: 700,
          outline: "none",
          padding: "0 8px",
          cursor: loading || companyContextLocked || companies.length < 2 ? "default" : "pointer",
          opacity: loading ? 0.65 : 1,
        }}
      >
        {companies.length === 0 ? (
          <option value={selectedId}>{loading ? "Loading..." : label}</option>
        ) : companies.map((company) => (
          <option key={company.id} value={company.id}>
            {company.company_code ? `${company.company_code} - ` : ""}{company.company_name || `Company ${company.id}`}
          </option>
        ))}
      </select>
    </div>
  );
};

const readCompanyTitleContext = () => {
  const user = readJson("erp_user") || {};
  return {
    companyName: localStorage.getItem("erp_company_name") || user.company_name || "Company",
    companyCode: localStorage.getItem("erp_company_code") || user.company_code || "",
    tenantName: localStorage.getItem("erp_tenant_name") || localStorage.getItem("tenant_code") || localStorage.getItem("erp_tenant_code") || user.tenant_name || "",
    accessRole: localStorage.getItem("company_access_role") || localStorage.getItem("erp_access_role") || user.access_role || "",
  };
};

const CompanyContextTitle = () => {
  const [context, setContext] = useState(readCompanyTitleContext);

  useEffect(() => {
    const refresh = () => setContext(readCompanyTitleContext());
    window.addEventListener(SELECTED_COMPANY_CONTEXT_EVENT, refresh);
    window.addEventListener(COMPANY_CONTEXT_EVENT, refresh);
    window.addEventListener("storage", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener(SELECTED_COMPANY_CONTEXT_EVENT, refresh);
      window.removeEventListener(COMPANY_CONTEXT_EVENT, refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  const subText = [
    context.companyCode,
    context.tenantName,
    String(context.accessRole || "").replace(/_/g, " "),
  ].filter(Boolean).join(" / ");
  const title = subText ? `${context.companyName} - ${subText}` : context.companyName;

  return (
    <div
      title={title}
      style={{
        minWidth: 0,
        maxWidth: "min(560px, 100%)",
        display: "flex",
        alignItems: "center",
        gap: 10,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: 7,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#93C5FD",
          background: "rgba(37,99,235,0.12)",
          border: "1px solid rgba(147,197,253,0.22)",
          boxShadow: "0 1px 0 rgba(255,255,255,0.05) inset",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 21h18" />
          <path d="M5 21V7l8-4v18" />
          <path d="M19 21V11l-6-4" />
        </svg>
      </div>
      <div style={{ minWidth: 0, overflow: "hidden" }}>
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.15,
            fontWeight: 800,
            color: "#E5E7EB",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            letterSpacing: 0,
          }}
        >
          {context.companyName}
        </div>
        {subText && (
          <div
            style={{
              marginTop: 2,
              fontSize: 10,
              lineHeight: 1.15,
              color: "#8EA6C8",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textTransform: "uppercase",
              letterSpacing: 0.3,
            }}
          >
            {subText}
          </div>
        )}
      </div>
    </div>
  );
};

const Topbar = () => {
  const navigate  = useNavigate();
  const { collapsed } = useSidebar();
  const { branding, getLogoUrl } = useBranding();
  const logoUrl = getLogoUrl();

  const rawUser  = localStorage.getItem("erp_user");
  const user     = rawUser ? JSON.parse(rawUser) : null;
  const username = user?.full_name || user?.username || localStorage.getItem("username") || "User";
  const role     = user?.role_name || user?.role || "User";

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const initials     = username.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2);
  const handleLogout = () => { localStorage.clear(); navigate("/login"); };
  const timeStr = formatSelectedCompanyDateTime(now, { hour: "2-digit", minute: "2-digit", hour12: true });
  const dateStr = formatSelectedCompanyDateTime(now, { day: "2-digit", month: "short", year: "numeric" });

  // Window controls — optional chaining for browser safety
  const winMin   = () => window.electronAPI?.windowMinimize?.();
  const winMax   = () => window.electronAPI?.windowMaximize?.();
  const winClose = () => window.electronAPI?.windowClose?.();

  return (
    // ── className="app-drag" makes topbar draggable in Electron ──
    <div
      className="app-drag erp-topbar"
      style={{
        height: "var(--erp-topbar-height)",
        background: "linear-gradient(90deg, #0A0F1E 0%, #1A2441 45%, #1E2D4A 70%, #141D36 100%)",
        borderBottom: "1px solid rgba(99,102,241,0.25)",
        display: "flex", alignItems: "center",
        boxShadow: "0 2px 16px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04) inset",
        userSelect: "none",
        position: "relative",
      }}
    >

      {/* ── Logo zone — ALWAYS shows logo, even when collapsed ── */}
      <div
        className="app-no-drag"
        style={{
          width: collapsed ? 56 : 220,
          minWidth: collapsed ? 56 : 220,
          height: "100%",
          display: "flex", alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: 8,
          padding: collapsed ? "0" : "0 12px",
          borderRight: "1px solid rgba(148,163,184,0.18)",
          transition: "width 0.22s cubic-bezier(.4,0,.2,1), min-width 0.22s cubic-bezier(.4,0,.2,1), padding 0.22s",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {/* Dynamic Logo — uploaded image if present, else letter mark */}
        {logoUrl ? (
          <img src={logoUrl} alt="logo" style={{
            width: 30, height: 30, borderRadius: 8, flexShrink: 0,
            objectFit: "contain",
            background: "rgba(255,255,255,0.08)",
            boxShadow: "0 2px 10px rgba(99,102,241,0.45)",
          }} />
        ) : (
          <div style={{
            width: 30, height: 30, borderRadius: 8, flexShrink: 0,
            background: `linear-gradient(135deg, ${branding.primary_color}, ${branding.secondary_color})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 15, fontWeight: 900, color: "#fff",
            boxShadow: "0 2px 10px rgba(99,102,241,0.55)",
            letterSpacing: "-0.5px",
          }}>{branding.logo_letter || "A"}</div>
        )}
        {/* Product name — hides on collapse */}
        {!collapsed && (
          <div style={{ overflow: "hidden" }}>
            <div style={{
              fontSize: 11, fontWeight: 800, color: "#F1F5F9",
              letterSpacing: "1px", whiteSpace: "nowrap", lineHeight: 1.2,
              background: "linear-gradient(90deg, #A5B4FC, #E879F9)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              {branding.app_name}
            </div>
          </div>
        )}
      </div>

      {/* ── Spacer — draggable (double-click = maximize) ──────── */}
      <div
        className="app-drag"
        style={{
          flex: 1,
          height: "100%",
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          padding: "0 22px",
          overflow: "hidden",
        }}
        onDoubleClick={winMax}
      >
        <CompanyContextTitle />
      </div>

      <CompanySelector />

      {/* ── Cache Status (Redis) ─────────────────────────────── */}
      <CacheStatus />

      {/* ── Notification Bell (M33) ──────────────────────────── */}
      <NotificationBell />

      {/* ── Alert Bell ───────────────────────────────────────── */}
      <AlertBell />

      {/* ── Clock ────────────────────────────────────────────── */}
      <div
        className="app-no-drag"
        style={{
          display: "flex", flexDirection: "column", alignItems: "flex-end",
          padding: "0 16px",
          borderLeft: "1px solid rgba(148,163,184,0.18)",
          height: "100%", justifyContent: "center",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: "#F1F5F9", fontVariantNumeric: "tabular-nums" }}>{timeStr}</div>
        <div style={{ fontSize: 10, color: "#94A3B8" }}>{dateStr}</div>
      </div>

      {/* ── User Info ──────────────────────────────────────────── */}
      <div
        className="app-no-drag"
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "0 12px", height: "100%",
          borderLeft: "1px solid rgba(148,163,184,0.18)",
          borderRight: "1px solid rgba(148,163,184,0.18)",
        }}
      >
        <div style={{
          width: 30, height: 30, borderRadius: "50%",
          background: "linear-gradient(135deg, #2563EB, #7C3AED)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, fontWeight: 800, color: "#fff", flexShrink: 0,
          boxShadow: "0 2px 6px rgba(37,99,235,0.4)",
        }}>{initials}</div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#E2E8F0", lineHeight: 1.2 }}>{username}</div>
          <div style={{ fontSize: 10, color: "#64748B", lineHeight: 1 }}>{role}</div>
        </div>
      </div>

      {/* ── Logout ──────────────────────────────────────────────── */}
      <button
        className="app-no-drag"
        onClick={handleLogout}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "0 14px", height: "100%",
          background: "transparent", border: "none", cursor: "pointer",
          color: "#94A3B8", fontSize: 12, fontWeight: 600,
          transition: "all 0.15s",
          borderRight: "1px solid rgba(148,163,184,0.18)",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#7F1D1D33"; e.currentTarget.style.color = "#FCA5A5"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#94A3B8"; }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
          <polyline points="16 17 21 12 16 7"/>
          <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
        Logout
      </button>

      {/* ── Window Controls ─────────────────────────────────────
          CRITICAL: use className="win-ctrl-btn app-no-drag"
          DO NOT use inline WebkitAppRegion — React ignores it  */}
      <div
        className="app-no-drag"
        style={{ display: "flex", height: "100%", borderLeft: "1px solid rgba(148,163,184,0.18)" }}
      >
        {/* Minimize */}
        <button
          className="win-ctrl-btn app-no-drag"
          onClick={winMin}
          title="Minimize"
        >
          <svg width="11" height="1" viewBox="0 0 11 1">
            <rect width="11" height="1.5" rx="1" fill="#94A3B8"/>
          </svg>
        </button>

        {/* Maximize / Restore */}
        <button
          className="win-ctrl-btn app-no-drag"
          onClick={winMax}
          title="Maximize / Restore"
        >
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
            <rect x="1" y="1" width="9" height="9" rx="1" stroke="#94A3B8" strokeWidth="1.3"/>
          </svg>
        </button>

        {/* Close */}
        <button
          className="win-ctrl-btn win-close app-no-drag"
          onClick={winClose}
          title="Close"
        >
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="#94A3B8" strokeWidth="1.4" strokeLinecap="round">
            <line x1="1.5" y1="1.5" x2="9.5" y2="9.5"/>
            <line x1="9.5" y1="1.5" x2="1.5" y2="9.5"/>
          </svg>
        </button>
      </div>

    </div>
  );
};

export default Topbar;
